import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, errorMessage } from '@/lib/api'
import Card, { SectionLabel } from '@/components/ui/Card'
import PageHeader from '@/components/ui/PageHeader'
import Toggle from '@/components/ui/Toggle'
import { useToast } from '@/components/ui/toast'
import LogoLoader from '@/components/ui/LogoLoader'

interface NotificationSettings {
  remind_late: boolean
  remind_due_today: boolean
  remind_due_tomorrow: boolean
  remind_email: boolean
}

const reminders: { key: keyof NotificationSettings; label: string; hint: string }[] = [
  {
    key: 'remind_late',
    label: 'Late jobs',
    hint: 'Sent at 8:00 am on working days. Tells staff about jobs their section should have finished already.',
  },
  {
    key: 'remind_due_today',
    label: 'Due today',
    hint: 'Sent at 8:00 am on working days. Tells staff about jobs their section must finish today.',
  },
  {
    key: 'remind_due_tomorrow',
    label: 'Due tomorrow',
    hint: 'Sent at 4:00 pm on working days. Tells staff about jobs their section must finish tomorrow.',
  },
]

/**
 * Switches for the reminders sent to each section's staff. Staff only hear
 * about their own section, once per run as a single digest, and nothing is
 * sent on a closed day or when a section has nothing to report. Each switch
 * saves as soon as it is flipped.
 */
export default function AdminNotifications() {
  const toast = useToast()
  const queryClient = useQueryClient()

  const settings = useQuery({
    queryKey: ['admin-notification-settings'],
    queryFn: async () => (await api.get<NotificationSettings>('/admin/notification-settings')).data,
  })

  const save = useMutation({
    mutationFn: async (body: Partial<NotificationSettings>) =>
      (await api.patch<NotificationSettings & { message: string }>('/admin/notification-settings', body)).data,
    onSuccess: (res) => {
      queryClient.setQueryData(['admin-notification-settings'], res)
      toast.success(res.message)
    },
    onError: (err) => toast.error('Save failed', errorMessage(err)),
  })

  if (settings.isLoading || !settings.data) return <LogoLoader />

  const current = settings.data

  return (
    <>
      <PageHeader title="Notifications" subtitle="Messages sent to production staff when a job is late or almost due" />

      <Card className="mb-5">
        <SectionLabel>What staff are told</SectionLabel>
        <p className="mb-4 text-sm text-foreground/60">
          Staff get one message on the bell and phone, listing only the jobs in their own section. Urgent jobs are
          listed first. Nothing is sent on days the factory is closed, or when there is nothing to tell.
        </p>
        <div className="flex flex-col gap-4">
          {reminders.map(({ key, label, hint }) => (
            <Toggle
              key={key}
              checked={current[key]}
              disabled={save.isPending}
              onChange={(checked) => save.mutate({ [key]: checked })}
              label={label}
              hint={hint}
            />
          ))}
        </div>
      </Card>

      <Card>
        <SectionLabel>Also send by email</SectionLabel>
        <Toggle
          checked={current.remind_email}
          disabled={save.isPending}
          onChange={(checked) => save.mutate({ remind_email: checked })}
          label="Send the same message by email"
          hint="Each staff member gets one email listing their jobs. If off, they only get the message on the bell and phone."
        />
      </Card>
    </>
  )
}
